import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QRCode from 'react-qr-code';

import {
  ITabletPairingStart,
  useCancelQrPairing,
  useGetQrPairingStatus,
  useStartQrPairing,
} from '../../hooks/reactQuerys/TabletQueries';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = 'enterName' | 'showQr' | 'success';

// QR-Pairing-Dialog:
// 1) Mitarbeiter vergibt einen Anzeigenamen
// 2) Dialog zeigt den vom Backend erzeugten QR
// 3) Tablet scannt -> Backend setzt ConsumedAt -> Dialog wechselt auf Success
export const PairTabletDialog: React.FC<Props> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>('enterName');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pairing, setPairing] = useState<ITabletPairingStart | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const { startQrPairing, isPendingStartQrPairing } = useStartQrPairing();
  const { cancelQrPairing } = useCancelQrPairing();
  const { pairingStatus } = useGetQrPairingStatus(
    pairing?.idTabletPairingCode ?? null,
    step === 'showQr'
  );

  // Countdown bis Ablauf
  useEffect(() => {
    if (step !== 'showQr' || !pairing) return undefined;
    const id = window.setInterval(() => {
      setSecondsLeft(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step, pairing]);

  // Reagiere auf Status-Polling
  useEffect(() => {
    if (!pairingStatus) return;
    if (pairingStatus.status === 'consumed') {
      setStep('success');
    } else if (pairingStatus.status === 'expired') {
      setError('Der QR-Code ist abgelaufen. Bitte einen neuen Code anfordern.');
    }
  }, [pairingStatus]);

  function reset() {
    setStep('enterName');
    setName('');
    setDescription('');
    setPairing(null);
    setSecondsLeft(0);
    setError(null);
  }

  async function handleClose() {
    // Wenn der Mitarbeiter vorzeitig abbricht: noch nicht eingeloesten
    // Code im Backend verwerfen.
    if (step === 'showQr' && pairing?.idTabletPairingCode) {
      try {
        await cancelQrPairing(pairing.idTabletPairingCode);
      } catch {
        // egal
      }
    }
    reset();
    onClose();
  }

  async function handleGenerate() {
    setError(null);
    if (!name.trim()) {
      setError('Bitte einen Anzeigenamen vergeben.');
      return;
    }
    try {
      const res = await startQrPairing({
        Name: name.trim(),
        Description: description.trim() || undefined,
      });
      setPairing(res);
      setSecondsLeft(res.expiresIn);
      setStep('showQr');
    } catch {
      setError('QR-Code konnte nicht erzeugt werden.');
    }
  }

  const noServerUrl = pairing?.serverUrl === '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Tablet koppeln</DialogTitle>
      <DialogContent>
        {step === 'enterName' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Vergeben Sie einen Anzeigenamen für das Tablet. Anschließend
              erscheint ein QR-Code, den Sie auf dem Tablet abscannen.
            </Typography>
            <TextField
              autoFocus
              label='Anzeigename'
              placeholder='z. B. Besprechungsraum 1'
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label='Beschreibung (optional)'
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            {error && <Alert severity='error'>{error}</Alert>}
          </Stack>
        )}

        {step === 'showQr' && pairing && (
          <Stack spacing={2} alignItems='center' sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Scannen Sie diesen Code mit der Tablet-App.
            </Typography>
            {noServerUrl && (
              <Alert severity='warning' sx={{ width: '100%' }}>
                <Typography variant='body2'>
                  Die Einstellung <b>Private.Link.DiNo</b> ist leer — der QR
                  enthält keine Server-URL. Bitte erst unter Einstellungen
                  hinterlegen, sonst weiß das Tablet nicht, wohin es sich
                  verbinden soll.
                </Typography>
              </Alert>
            )}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                width: 280,
                height: 280,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <QRCode
                value={pairing.qrPayload}
                size={248}
                level='M'
                style={{ height: 'auto', width: '100%', maxWidth: '100%' }}
              />
            </Box>
            <Typography variant='caption' color='text.secondary'>
              Anzeigename: <b>{pairing.name}</b>
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Gültig noch {Math.floor(secondsLeft / 60)}:
              {String(secondsLeft % 60).padStart(2, '0')} Min
            </Typography>

            {pairing.shortCode && (
              <Box
                sx={{
                  width: '100%',
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  border: theme => `1px dashed ${theme.palette.divider}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mb: 0.5 }}
                >
                  Falls QR-Scan nicht funktioniert (z. B. Kamera defekt) —
                  diesen Einwahlcode am Tablet manuell eingeben:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '1.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    userSelect: 'all',
                  }}
                >
                  {pairing.shortCode}
                </Typography>
              </Box>
            )}
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ color: 'text.secondary' }}
            >
              <CircularProgress size={14} />
              <Typography variant='caption'>
                Warte auf Tablet-Verbindung …
              </Typography>
            </Stack>
            {error && (
              <Alert severity='error' sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}

        {step === 'success' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity='success'>
              Das Tablet „<b>{pairing?.name}</b>" wurde erfolgreich gekoppelt
              und erscheint nun in der Liste.
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'enterName' && (
          <>
            <Button onClick={handleClose}>Abbrechen</Button>
            <Button
              variant='contained'
              onClick={handleGenerate}
              disabled={isPendingStartQrPairing}
            >
              QR-Code erzeugen
            </Button>
          </>
        )}
        {step === 'showQr' && <Button onClick={handleClose}>Abbrechen</Button>}
        {step === 'success' && (
          <Button variant='contained' onClick={handleClose}>
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
